import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AmorgAdminToken, getDefaultCompanyInfo, OrganizationListView, rng } from '../../amorg-common';
import { createOrganization, getActiveUser, getOrganizations, updateOrganization } from '../../api/adminClientApi';
import { AdminTab } from './_components/AdminTab';
import { AmorgAdminPageProvider } from './AmorgAdminPageContext';
import { LoadingPageOverlay } from '../../designSystem/LoadingPageOverlay';
import { SingleColumnLayout } from '../../designSystem/layouts/SingleColumnLayout';
import { Flex } from '../../designSystem/compositeComponents/FlexHelpers';
import { TextField } from '../../designSystem/formField/text/TextField';
import { Table, TableRowWithOnClick } from '../../designSystem/Table';
import { MessageCard } from '../../designSystem/Message';
import { Button } from '../../designSystem/button/Button';

export default function AmorgAdminOrganizations (): JSX.Element
{
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState(true);
    const [searchTerm, setSearchTerm] = React.useState('');
    const [organizations, setOrganizations] = React.useState<OrganizationListView[]>([]);
    const [activeUser, setActiveUser] = React.useState<AmorgAdminToken | null>(null);
    const [error, setError] = React.useState('');

    React.useEffect(() =>
    {
        document.title = 'Amorgy - Organisationer';
        const init = async () =>
        {
            try
            {
                const token = await getActiveUser(() => navigate('/amorgy/login'));
                setActiveUser(token);
                setOrganizations(await getOrganizations(() => navigate('/amorgy/login')));
            }
            catch
            {
                setError('Organisationer kunne ikke indlæses');
            }
            finally
            {
                setLoading(false);
            }
        };
        init();
    }, []);

    const filteredOrganizations = organizations.filter(organization =>
        organization.companyInfo.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        organization.partner?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        organization.sites.some(siteId => siteId.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    async function handleCreateOrganization ()
    {
        setLoading(true);
        setError('');

        const organizationId = rng.getRandomId('org');
        const created = await createOrganization(organizationId, () => navigate('/amorgy/login'));
        if(!created)
        {
            setError('Organisationen kunne ikke oprettes');
            setLoading(false);
            return;
        }

        const defaultCompanyInfo = { ...getDefaultCompanyInfo(), name: 'Ny organisation' };
        const updated = await updateOrganization(
            organizationId,
            defaultCompanyInfo,
            activeUser?.partner || null,
            () => navigate('/amorgy/login')
        );
        if(!updated)
        {
            // No need to provide error message here
            return;
        }

        setOrganizations(await getOrganizations(() => navigate('/amorgy/login')));
        setLoading(false);
        navigate(`/amorgy/organizations/${organizationId}/companyInfo`);
    }

    return (
        <AmorgAdminPageProvider>
            <LoadingPageOverlay loading={loading} />
            <SingleColumnLayout
                noDivider
                headerContainer={
                    <Flex flexDirection="column" gap={16}>
                        <AdminTab selectedTab="organizations" />
                    </Flex>
                }
                contentContainer={
                    <Flex flexDirection="column" gap={16}>
                        {error && <MessageCard variant="error">{error}</MessageCard>}

                        <div style={{ maxWidth: '400px' }}>
                            <TextField
                                value={searchTerm}
                                onChange={value => setSearchTerm(value)}
                                hideLabel
                                leadingIcon="interface_actions_search"
                                placeholder="Søg" />
                        </div>

                            <Flex justifyContent="flex-end">
                                <Button
                                variant="text"
                                text="Tilføj organisation"
                                icon="actions_plus"
                                onClick={handleCreateOrganization} />
                            </Flex>

                        <Table>
                            <thead>
                                <tr>
                                    <th>Organisation</th>
                                    {activeUser?.partner === null && <th>Partner</th>}
                                    <th>Sites</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredOrganizations.map(organization =>
                                    <TableRowWithOnClick
                                        key={organization.id}
                                        onClick={() => navigate(`/amorgy/organizations/${organization.id}/companyInfo`)}>
                                        <td>
                                            {organization.companyInfo.name
                                                ?   (organization.companyInfo.name)
                                                :   <i>-</i>
                                            }
                                        </td>
                                        {activeUser?.partner === null &&
                                            <td>{organization.partner}</td>
                                        }
                                        <td>{organization.sites.length}</td>
                                    </TableRowWithOnClick>
                                )}
                            </tbody>
                        </Table>
                    </Flex>
                } />
        </AmorgAdminPageProvider>
    );
}